/**
 * The funding copy Outlast delivered.
 *
 * The award amounts and the committed total are theirs, transcribed as written.
 * Their sentence dates the total, "as of [date]". Outlast asked for a season
 * and a year rather than a day, and for it not to be derived from the clock.
 */
export const marketingFundData = {
  committed:
    "We have $700,000 committed funds as of late 2026 in addition to commissioned Research with our lab.",
  awards: [
    {
      amount: "$350,000",
      name: "Peer Awards",
      description: "For Pollinator participants\n(ranging from $500-$10,000)",
    },
    {
      amount: "$30,000",
      name: "Community Builder Award",
      description: "For Pollinator participants",
    },
    {
      amount: "$85,000",
      name: "Social Impact Award",
      description: "For Pollinator participants",
    },
    {
      amount: "$85,000",
      name: "Award for Artist-led Institutions + Trans-local Networks",
      description: "For Pollinator participants",
    },
    {
      amount: "$30,000",
      name: "Arts Advocacy Award",
      description: "For Pollinator participants",
    },
    {
      amount: "+ more",
      name: "Partner with us to source grantees and support peer connection.",
      description: "",
      // The funder's way in, as the Fund section's Request a Meeting is:
      // these opening words of the name are its link.
      href: "/partner-with-us",
      linkText: "Partner with us",
    },
  ],
};

/**
 * A partner's mark on a grantmaker card.
 *
 * Each is drawn at the size the design gives it, in pixels, at every width.
 * They are not one height: a wide wordmark is set shorter than a square mark
 * so that the row of them reads as even. `isMuted` is for a mark the design
 * sets at half strength, where at full strength it would outweigh the others.
 * A mark whose file was exported already at half strength (Jerome, Doris
 * Duke) is not marked, or it would be halved twice.
 */
export type GrantmakerLogo = {
  logo: string;
  /** The partner the mark belongs to, where a card carries more than one */
  name?: string;
  width: number;
  height: number;
  isMuted?: boolean;
};

/**
 * The grantmaker work, as the design lists it: nine pieces of work, each with
 * the partner's own mark, which is the only thing on a card that identifies
 * them at a glance. A piece of work done with two partners carries both marks.
 */
export const marketingGrantmakerWorkData: {
  kind: string;
  partner: string;
  description: string;
  logos: GrantmakerLogo[];
}[] = [
  {
    kind: "Sourcing",
    partner: "Wagner Foundation + ArtsFirst",
    logos: [
      { logo: "wagnerFoundation", name: "Wagner Foundation", width: 75, height: 32, isMuted: true },
      { logo: "artsFirst", name: "ArtsFirst", width: 75, height: 32, isMuted: true },
    ],
    description:
      "Building a national directory of artist-run spaces and networks to enable discovery and collaboration for Wagner and supporting ArtsFirst in implementing their grantmaking strategy with precision.",
  },
  {
    kind: "Award + Implementation",
    partner: "Halloran Foundation",
    logos: [{ logo: "halloranPhilanthropies", width: 105, height: 28 }],
    description:
      "To achieve greater equity and transparency in grantmaking, we operationalized peer-to-peer, relational approaches to sourcing, funding, and evaluation.",
  },
  {
    kind: "Convening",
    partner: "ImpactPHL",
    logos: [{ logo: "impactPHL", width: 63, height: 32 }],
    description:
      "We analyze and unlock the latent community behind their annual events, matching 500+ participants into small groups for niche, personalized conversations",
  },
  {
    kind: "Evaluation",
    partner: "Foundation for Contemporary Arts",
    logos: [{ logo: "foundationForContemporaryArts", width: 66, height: 28 }],
    description:
      "We offered peer-to-peer development to alumni; 94.5% expressed interest in trying it in the future.",
  },
  {
    kind: "Longitudinal Connection",
    partner: "Jerome Foundation",
    logos: [{ logo: "jeromeFoundation", width: 99, height: 32 }],
    description:
      "We offer support beyond grant dollars, creating a secure hub for grantees to find one another and shared resources.",
  },
  {
    kind: "Ecosystem Infrastructure",
    partner: "Kataly Foundation",
    logos: [{ logo: "katalyFoundation", width: 61, height: 32 }],
    description:
      "Building secure peer infrastructure for mutual support and coordinated action.",
  },
  {
    kind: "Ecosystem Infrastructure",
    partner: "Doris Duke Foundation",
    logos: [{ logo: "dorisDuke", width: 27, height: 32 }],
    description:
      "Building secure peer infrastructure for mutual support and coordinated action.",
  },
  {
    kind: "Ecosystem Infrastructure",
    partner: "Swiss Philanthropy",
    logos: [{ logo: "swissPhilanthropiesFoundation", width: 104, height: 32 }],
    description:
      "Building secure peer infrastructure for mutual support and coordinated action.",
  },
];

/**
 * The network snapshot.
 *
 * Nine figures, which fill the panel's three columns. Each is a placeholder
 * here and counted by the backend; one it cannot count stays a dash rather
 * than being invented, so it is visible as unfilled to whoever reviews the
 * page.
 */
export const networkSnapshot = [
  { value: "93%", label: "Participants are Facilitators, Teachers, or Directors." },
  { value: "—", label: "Participants are members of Collectives or Artist-Run Spaces." },
  { value: "—", label: "Researchers + Faculty" },
  { value: "—", label: "Fulbright Fellows" },
  { value: "—", label: "Guggenheim Fellows" },
  { value: "—", label: "Founders + Org Leaders" },
  { value: "—", label: "Countries" },
  { value: "—", label: "Joined through a peer’s invitation" },
  { value: "—", label: "Peer groups" },
];

/**
 * The figures the copy quotes about the network, as Outlast's copy gives them.
 * Shown until the counted ones arrive, and if they never do.
 */
export const networkCounts = {
  participants: "7,139",
  organizations: "400",
  sharingShare: "97%",
};
